import { useState } from "react";

import { Box, Stack } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { usePostHog } from "posthog-js/react";

import { EvotruxDivider } from "components/EvotruxDivider";
import { useShipperNetwork, useShipperPreferences } from "hooks";
import { scrollMainContentToTop } from "utils";

import { ClientAppEvent } from "../../../../constants";
import { CreateShipmentForm } from "../CreateShipmentForm";
import { ShipmentTemplateMenu } from "../ShipmentTemplateMenu";
import { getCreateShipmentFormStateFromTemplateContent } from "../ShipmentTemplateMenu/utils";

import type { CreateShipmentFormState, ShipmentTemplate } from "types/shipment";

interface Props {
  refreshShipments: VoidFunction;
  triggerScrollToShipments?: VoidFunction;
}

export function ShipmentTemplateAndCreateShipmentFormDashboardItem(
  props: Props,
) {
  const { refreshShipments, triggerScrollToShipments } = props;

  const [shipmentState, setShipmentState] = useState<
    undefined | CreateShipmentFormState
  >(undefined);

  const { preferences } = useShipperPreferences({
    fallbackFormView: "PUBLIC_SHIPMENT",
  });
  const posthog = usePostHog();

  const { loading: loadingNetwork } = useShipperNetwork();

  const onTemplateClicked = (template: ShipmentTemplate) => {
    posthog?.capture(ClientAppEvent.SHIPMENT_TEMPLATE_APPLIED, {
      template_id: template?.id || undefined,
      template_name: template?.name || undefined,
      has_route_points:
        template?.content?.route && template.content.route.length > 0,
      equipment_types_count: template?.content?.equipment_types?.length ?? 0,
      source: "PLATFORM_DASHBOARD",
    });
    const createFormState = getCreateShipmentFormStateFromTemplateContent({
      template,
    });
    setShipmentState(createFormState);
  };

  return (
    <Stack direction="column" mr={preferences == null ? "2rem" : undefined}>
      <ShipmentTemplateMenu
        onTemplateClick={onTemplateClicked}
        waitingOnCallerData={loadingNetwork}
      />
      <Box mb="1rem" mt="1rem">
        <EvotruxDivider />
      </Box>
      {preferences != null ? (
        <CreateShipmentForm
          postShipmentUrl="/shipments"
          shipmentState={shipmentState}
          onSubmitShipmentSuccessCallback={() => {
            refreshShipments();
            if (triggerScrollToShipments) {
              triggerScrollToShipments();
            } else {
              scrollMainContentToTop(0);
            }
          }}
          showShareMenu
        />
      ) : (
        <Stack direction="column" width="100%">
          <Skeleton variant="text" animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </Stack>
      )}
    </Stack>
  );
}
